import React, { useState, useRef, useEffect } from 'react';
import { MdClose, MdSearch } from 'react-icons/md'; // Import search icon

const ProductAttributes = ({ onAttributesChange }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newValue, setNewValue] = useState('');
  const textareaRef = useRef(null);
  const popupRef = useRef(null);

  const allAttributes = [
    { label: 'Care', values: ['Dry Clean', 'Hand Wash', 'Machine Wash'] },
    { label: 'Length', values: ['Approx. 6.5 M', 'Approx. 5.5 M'] },
    { label: 'Blouse piece', values: ['Yes', 'No'] },
    { label: 'Dual tone', values: ['Yes', 'No'] },
   
    // Add many more attributes as needed
  ];

  const filteredAttributes = allAttributes.filter((attribute) => {
    const labelMatch = attribute.label.toLowerCase().includes(searchTerm.toLowerCase());
    const valueMatch = attribute.values.some((value) =>
      value.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return labelMatch || valueMatch;
  });

  const handleTextareaFocus = () => {
    setIsPopupOpen(true);
  };

  const handleTextareaBlur = () => {
    setTimeout(() => {
      if (!textareaRef.current?.contains(document.activeElement) && !popupRef.current?.contains(document.activeElement)) {
        setIsPopupOpen(false);
        setSearchTerm('');
        setNewLabel('');
        setNewValue('');
      }
    }, 100);
  };

  const handleAttributeSelect = (label, value) => {
    const isDuplicate = selectedAttributes.some(
      (attr) => attr.label === label && attr.value === value
    );

    if (!isDuplicate) {
      setSelectedAttributes((prevAttributes) => [...prevAttributes, { label, value }]);
    }
    setIsPopupOpen(false);
    setSearchTerm('');
    setNewLabel('');
    setNewValue('');
  };

  const handleRemoveAttribute = (index) => {
    setSelectedAttributes((prevAttributes) => prevAttributes.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCreateNewAttribute = () => {
    if (newLabel && newValue) {
      const isDuplicate = selectedAttributes.some(
        (attr) => attr.label.toLowerCase() === newLabel.toLowerCase() && attr.value.toLowerCase() === newValue.toLowerCase()
      );
      if (!isDuplicate) {
        setSelectedAttributes((prevAttributes) => [...prevAttributes, { label: newLabel, value: newValue }]);
        if (onAttributesChange) {
          onAttributesChange([...selectedAttributes, { label: newLabel, value: newValue }]);
        }
        setIsPopupOpen(false); // Close the popup after adding
      }
      setNewLabel('');
      setNewValue('');
    }
  };

  useEffect(() => {
    if (onAttributesChange) {
      onAttributesChange(selectedAttributes);
    }
  }, [selectedAttributes, onAttributesChange]);

  return (
    <div className="relative">
      <div className="w-full p-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-300 min-h-[40px] flex flex-wrap items-center gap-2">
        {selectedAttributes.map((attr, index) => (
          <div
            key={index}
            className="bg-blue-100 text-blue-700 rounded-full px-2 py-1 text-xs flex items-center gap-1"
          >
            {attr.label}: {attr.value}
            <button
              type="button"
              className="text-blue-500 hover:text-blue-700 focus:outline-none"
              onClick={() => handleRemoveAttribute(index)}
            >
              <MdClose className="h-4 w-4" />
            </button>
          </div>
        ))}
        <textarea
          ref={textareaRef}
          className="flex-grow focus:outline-none text-sm placeholder-gray-500"
          value=""
          onFocus={handleTextareaFocus}
          onBlur={handleTextareaBlur}
          onKeyDown={handleKeyDown}
          placeholder={selectedAttributes.length > 0 ? "" : "Click here to add product attributes"}
        />
      </div>

      {isPopupOpen && (
        <div
          ref={popupRef}
          className="absolute left-0 mt-2 bg-white border rounded-md shadow-md z-10 w-72 md:w-96"
        >
          <div className="p-3 border-b">
            <div className="relative flex items-center mb-2">
              <MdSearch className="absolute left-2 text-gray-400" />
              <input
                type="text"
                placeholder="Search attributes..."
                className="w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-300 text-sm"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            {/* Input fields to create new attribute */}
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="New Label"
                className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-300"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
              <input
                type="text"
                placeholder="New Value"
                className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-300"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              />
            </div>
            <button
              className="mt-2 bg-green-500 hover:bg-green-600 text-white font-semibold py-1 px-2 rounded-md text-sm focus:outline-none focus:ring focus:ring-green-300"
              onClick={handleCreateNewAttribute}
              type="button"
            >
              Add New
            </button>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto">
            {filteredAttributes.length > 0 ? (
              filteredAttributes.map((attribute) => (
                <div key={attribute.label} className="mb-2">
                  <label className="block text-sm font-semibold text-gray-700">{attribute.label}</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {attribute.values.map((value) => (
                      <button
                        key={value}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full px-2 py-1 text-xs focus:outline-none"
                        onClick={() => handleAttributeSelect(attribute.label, value)}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No attributes found matching your search.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAttributes;